import React from 'react';
import Model from 'react-body-highlighter';

// Map Masse-terclass French muscles to react-body-highlighter English muscles
const MUSCLE_MAP = {
  'Épaules': ['front-deltoids', 'back-deltoids'],
  'Biceps': ['biceps'],
  'Triceps': ['triceps'],
  'Avant-bras': ['forearm'],
  'Dos': ['upper-back', 'lower-back', 'trapezius'],
  'Pectoraux': ['chest'],
  'Abdos': ['abs', 'obliques'],
  'Quadriceps': ['quadriceps'],
  'Ischio-jambiers': ['hamstring'],
  'Jambes': ['quadriceps', 'hamstring'],
  'Mollets': ['calves'],
  'Fessier': ['gluteal'],
  'Cou': ['neck'],
  'Cardio': [],
  'Callisthénie': [],
  'Autre': []
};

const MuscleHeatmap = ({ recentWorkouts = [], exercisesMap = {} }) => {
  const muscleRecoveryMap = {}; // { [muscleName]: mostRecentWorkoutTime }
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // 1. Trouver la date d'entrainement la plus récente pour chaque muscle
  recentWorkouts.forEach(workout => {
    const workoutDate = new Date(workout.date);
    workoutDate.setHours(0, 0, 0, 0);
    const workoutTime = workoutDate.getTime();

    workout.exercises?.forEach(block => {
      const items = block.type === 'superset' ? block.items : [block];
      items.forEach(ex => {
        let globalEx = exercisesMap[ex.exerciseId];
        if (!globalEx) {
          globalEx = Object.values(exercisesMap).find(e => e.name === ex.name) || {};
        }
        
        const guessMuscleFromName = (name) => {
          if (!name) return 'Autre';
          const lowerName = name.toLowerCase();
          if (/(curl|ischio|soulevé|souleve|deadlift|sdt|good morning|rdl|arrière cuisse|arriere cuisse)/.test(lowerName)) return 'Ischio-jambiers';
          if (/(squat|presse|leg|fente|quad|jambe)/.test(lowerName)) return 'Quadriceps';
          if (/(mollet|calf)/.test(lowerName)) return 'Mollets';
          if (/(hip thrust|fessier|glute|abduct)/.test(lowerName)) return 'Fessier';
          if (/(traction|rowing|dos|pull|lat|tirage)/.test(lowerName)) return 'Dos';
          if (/(pec|développé|bench|push|pompe|fly|chest|écarté)/.test(lowerName)) return 'Pectoraux';
          if (/(épaule|shoulder|élévation|military|face pull)/.test(lowerName)) return 'Épaules';
          if (/(bicep|curl)/.test(lowerName)) return 'Biceps';
          if (/(tricep|extension|dips)/.test(lowerName)) return 'Triceps';
          if (/(abdo|crunch|gainage|core|planche)/.test(lowerName)) return 'Abdos';
          return 'Autre';
        };

        let rawMuscles = globalEx.muscles || (globalEx.muscle ? [globalEx.muscle] : null) || ex.muscles || (ex.muscle ? [ex.muscle] : null);
        if (!rawMuscles) {
          rawMuscles = [guessMuscleFromName(ex.name)];
        }
        
        const baseMusclesArray = Array.isArray(rawMuscles) ? rawMuscles : [rawMuscles];
        const frenchMuscles = baseMusclesArray.map(m => {
          if (m === 'Jambes') {
            const lowerName = ex.name.toLowerCase();
            if (/(curl|ischio|soulevé|souleve|deadlift|sdt|good morning|rdl|arrière cuisse|arriere cuisse)/.test(lowerName)) return 'Ischio-jambiers';
            if (/(hip thrust|fessier|glute|abduct)/.test(lowerName)) return 'Fessier';
            return 'Quadriceps';
          }
          return m;
        });
        
        frenchMuscles.forEach(muscle => {
          if (muscle && MUSCLE_MAP[muscle]) {
            MUSCLE_MAP[muscle].forEach(mappedMuscle => {
              if (!muscleRecoveryMap[mappedMuscle] || workoutTime > muscleRecoveryMap[mappedMuscle]) {
                muscleRecoveryMap[mappedMuscle] = workoutTime;
              }
            });
          }
        });
      });
    });
  });

  const heatmapData = [];

  // 2. Calculer le délai et assigner une fréquence (couleur)
  Object.keys(muscleRecoveryMap).forEach(muscle => {
    const lastWorkoutTime = muscleRecoveryMap[muscle];
    const diffTime = today.getTime() - lastWorkoutTime;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    let frequency = 0;
    if (diffDays <= 1) {
      frequency = 4; // Rouge (Hier ou Aujourd'hui)
    } else if (diffDays === 2) {
      frequency = 3; // Orange
    } else if (diffDays === 3) {
      frequency = 2; // Jaune
    } else if (diffDays >= 4 && diffDays <= 14) {
      frequency = 1; // Vert
    }

    if (frequency > 0) {
      heatmapData.push({
        name: muscle,
        muscles: [muscle],
        frequency: frequency
      });
    }
  });

  return (
    <div style={{ padding: '1rem', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <h3 style={{ marginTop: 0, textAlign: 'center', color: 'var(--primary-color)' }}>
        Récupération Musculaire
      </h3>
      <div style={{ textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span><span style={{ color: '#ff4757', fontWeight: 'bold' }}>●</span> 0-1j</span>
          <span><span style={{ color: '#ffa502', fontWeight: 'bold' }}>●</span> 2j</span>
          <span><span style={{ color: '#eccc68', fontWeight: 'bold' }}>●</span> 3j</span>
          <span><span style={{ color: '#2ed573', fontWeight: 'bold' }}>●</span> 4j+</span>
        </div>
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap', flex: 1, alignItems: 'center' }}>
        <div style={{ width: '45%', maxWidth: '190px', pointerEvents: 'none' }}>
          <Model
            data={heatmapData}
            style={{ width: '100%' }}
            highlightedColors={['#2ed573', '#eccc68', '#ffa502', '#ff4757']}
            type="anterior"
          />
        </div>
        <div style={{ width: '45%', maxWidth: '190px', pointerEvents: 'none' }}>
          <Model
            data={heatmapData}
            style={{ width: '100%' }}
            highlightedColors={['#2ed573', '#eccc68', '#ffa502', '#ff4757']}
            type="posterior"
          />
        </div>
      </div>
    </div>
  );
};

export default MuscleHeatmap;
